type CollectionOption = {
  id: number;
  name: string;
  has_children: boolean;
};

type InitialData = {
  current_collection_id: number | null;
  collection_path: CollectionOption[];
};

export class CascadingCollectionsController {
  private level0: HTMLSelectElement | null = null;
  private level1: HTMLSelectElement | null = null;
  private level2: HTMLSelectElement | null = null;
  private hiddenInput: HTMLInputElement | null = null;
  private container: HTMLElement | null = null;
  private cachedSubcollections: Map<number, CollectionOption[]> = new Map();
  private onChangeCallback: ((collectionId: number | null) => void) | null =
    null;
  private hydrationPromise: Promise<void> = Promise.resolve();

  constructor(container: HTMLElement) {
    this.container = container;
    this.cacheElements();
    this.hydrationPromise = this.loadInitialData();
    this.bindEvents();
  }

  ready(): Promise<void> {
    return this.hydrationPromise;
  }

  setOnChange(callback: (collectionId: number | null) => void): void {
    this.onChangeCallback = callback;
  }

  private cacheElements(): void {
    this.level0 = this.container?.querySelector("#collection-level-0") ?? null;
    this.level1 = this.container?.querySelector("#collection-level-1") ?? null;
    this.level2 = this.container?.querySelector("#collection-level-2") ?? null;
    this.hiddenInput =
      this.container?.querySelector("#test-collection-id") ?? null;
  }

  private async loadInitialData(): Promise<void> {
    const dataScript = document.getElementById("initial-collection-data");
    if (!dataScript) return;

    try {
      const data: InitialData = JSON.parse(dataScript.textContent || "{}");

      if (data.collection_path && data.collection_path.length > 0) {
        await this.hydrateFromPath(
          data.collection_path,
          data.current_collection_id,
        );
      }
    } catch (e) {
      console.error("Failed to parse initial collection data:", e);
    }
  }

  private async hydrateFromPath(
    path: CollectionOption[],
    currentId: number | null,
  ): Promise<void> {
    if (path.length === 0) return;

    const path0 = path[0];
    const path1 = path[1];
    const path2 = path[2];

    if (this.level0 && path0) {
      this.level0.value = String(path0.id);
    }

    if (path.length > 1 && path0 && path1) {
      await this.loadAndPopulateLevel(1, path0.id, path1.id);
    }

    if (path.length > 2 && path1 && path2) {
      await this.loadAndPopulateLevel(2, path1.id, path2.id);
    }

    this.updateHiddenInput(currentId);
  }

  private async loadAndPopulateLevel(
    level: number,
    parentId: number,
    selectedId?: number,
  ): Promise<void> {
    const select = level === 1 ? this.level1 : this.level2;
    if (!select) return;

    const subcollections = await this.fetchSubcollections(parentId);
    this.populateSelect(select, subcollections);

    if (selectedId) {
      select.value = String(selectedId);
      select.classList.remove("hidden");
    }
  }

  private bindEvents(): void {
    this.level0?.addEventListener("change", () => this.handleLevelChange(0));
    this.level1?.addEventListener("change", () => this.handleLevelChange(1));
    this.level2?.addEventListener("change", () => this.handleLevelChange(2));
  }

  private async handleLevelChange(level: number): Promise<void> {
    const currentSelect = this.getSelectForLevel(level);
    if (!currentSelect) return;

    const selectedId = currentSelect.value
      ? parseInt(currentSelect.value, 10)
      : null;

    this.clearDeeperLevels(level);

    if (selectedId === null) {
      const parentId = this.getDeepestSelectedId();
      if (parentId !== null) {
        const parentHasChildren = await this.checkHasChildren(parentId);
        if (parentHasChildren) {
          this.updateHiddenInput(null);
          return;
        }
      }
      this.updateHiddenInput(parentId);
      return;
    }

    const hasChildren = await this.checkHasChildren(selectedId, currentSelect);

    if (hasChildren && level < 2) {
      const nextSelect = this.getSelectForLevel(level + 1);
      if (nextSelect) {
        const subcollections = await this.fetchSubcollections(selectedId);
        this.populateSelect(nextSelect, subcollections);
        nextSelect.classList.remove("hidden");
      }
      this.updateHiddenInput(null);
    } else {
      this.updateHiddenInput(selectedId);
    }
  }

  private getSelectForLevel(level: number): HTMLSelectElement | null {
    switch (level) {
      case 0:
        return this.level0;
      case 1:
        return this.level1;
      case 2:
        return this.level2;
      default:
        return null;
    }
  }

  private clearDeeperLevels(fromLevel: number): void {
    for (let i = fromLevel + 1; i <= 2; i++) {
      const select = this.getSelectForLevel(i);
      if (select) {
        select.classList.add("hidden");
        select.innerHTML = '<option value="">Select a sub-category...</option>';
      }
    }
  }

  private getDeepestSelectedId(): number | null {
    for (let i = 2; i >= 0; i--) {
      const select = this.getSelectForLevel(i);
      if (select && select.value) {
        return parseInt(select.value, 10);
      }
    }
    return null;
  }

  private async checkHasChildren(
    collectionId: number,
    selectElement?: HTMLSelectElement,
  ): Promise<boolean> {
    if (selectElement) {
      const selectedOption = selectElement.options[selectElement.selectedIndex];
      if (selectedOption && selectedOption.dataset.hasChildren !== undefined) {
        return selectedOption.dataset.hasChildren === "true";
      }
    }

    const cached = this.cachedSubcollections.get(collectionId);
    if (cached !== undefined) {
      return cached.length > 0;
    }

    const subcollections = await this.fetchSubcollections(collectionId);
    return subcollections.length > 0;
  }

  private async fetchSubcollections(
    parentId: number,
  ): Promise<CollectionOption[]> {
    const cached = this.cachedSubcollections.get(parentId);
    if (cached !== undefined) {
      return cached;
    }

    try {
      const response = await fetch(
        `/api/collections/${parentId}/subcollections`,
        {
          headers: {
            Accept: "application/json",
          },
        },
      );

      if (!response.ok) {
        return [];
      }

      const data: CollectionOption[] = await response.json();
      this.cachedSubcollections.set(parentId, data);
      return data;
    } catch (error) {
      console.error("Failed to fetch subcollections:", error);
      return [];
    }
  }

  private populateSelect(
    select: HTMLSelectElement,
    options: CollectionOption[],
  ): void {
    select.innerHTML = '<option value="">Select a sub-category...</option>';
    options.forEach((option) => {
      const optEl = document.createElement("option");
      optEl.value = String(option.id);
      optEl.textContent = option.name;
      optEl.dataset.hasChildren = String(option.has_children);
      select.appendChild(optEl);
    });
  }

  private updateHiddenInput(collectionId: number | null): void {
    if (this.hiddenInput) {
      this.hiddenInput.value = collectionId ? String(collectionId) : "";
    }

    if (this.onChangeCallback) {
      this.onChangeCallback(collectionId);
    }
  }

  showError(): void {
    const errorEl = this.container?.querySelector("#collection-error");
    if (errorEl) {
      errorEl.classList.remove("hidden");
    }
    [this.level0, this.level1, this.level2].forEach((select) => {
      if (select && !select.classList.contains("hidden")) {
        select.classList.add("border-red-500");
      }
    });
  }

  hideError(): void {
    const errorEl = this.container?.querySelector("#collection-error");
    if (errorEl) {
      errorEl.classList.add("hidden");
    }
    [this.level0, this.level1, this.level2].forEach((select) => {
      if (select) {
        select.classList.remove("border-red-500");
      }
    });
  }

  getSelectedCollectionId(): number | null {
    const value = this.hiddenInput?.value;
    return value ? parseInt(value, 10) : null;
  }
}
